import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { locationsIDs, Theme } from '@constants';
import { useContextReference, useNodeHandle } from '@hooks';

import { isWeb } from '../../../helpers/Platform';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { TWCTextInput } from '../../primitives/TWCTextInput/TWCTextInput';

const tPath = 'locations.editLocationPanel';

export const EditLocationPanel = ({ setLocationName, setVisiblePanel, data, locationID }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const locationData = data.find(location => location.id === locationID);
  const initialLocation = locationData?.name ?? locationData?.fullName ?? '';

  const [locationName, setTextLocationName] = useState(initialLocation);
  const nameTextInputReference = useContextReference(locationsIDs.editLocationTextInput);
  const saveButtonReference = useRef(null);
  const cancelButtonReference = useRef(null);

  const nameTextInputHandle = useNodeHandle(nameTextInputReference);
  const saveButtonHandle = useNodeHandle(saveButtonReference);
  const cancelButtonHandle = useNodeHandle(cancelButtonReference);

  const goBack = () => {
    setVisiblePanel({ key: 'list' });
  };

  return (
    <View style={styles.panel}>
      <TWCText
        fontFamily={'light'}
        importantForAccessibility={'no'}
        numberOfLines={1}
        style={styles.titleText}
        testID={locationsIDs.editLocationHeaderText}
      >
        {t('headingText', {
          locationName: initialLocation,
          interpolation: { escapeValue: false },
        })}
      </TWCText>
      <TWCText fontFamily={'light'} style={styles.fullNameText} testID={locationsIDs.editLocationFullName}>
        {locationData?.fullName}
      </TWCText>
      <TWCText
        fontFamily={'light'}
        importantForAccessibility={'no'}
        style={styles.subTitleText}
        testID={locationsIDs.editLocationSubHeadingText}
      >
        {t('editSubtileText')}
      </TWCText>
      <TWCTextInput
        accessibilityHint={t('editAccessibilityHint')}
        accessibilityLabel={t('editAccessibilityLabel', {
          name: locationName,
          locationName: initialLocation,
          interpolation: { escapeValue: false },
        })}
        defaultValue={initialLocation}
        hideKeyboardInput
        keyboardBackground={Theme.withOpacity(Theme.blackPearlBlue, 0.8)}
        keyboardDismissConfig={{ right: true }}
        keyboardWidth={740}
        keyboardWrapConfig={{ horizontal: false }}
        nextFocusDown={saveButtonHandle}
        offsetX={Theme.sidePanelWidth - Theme.appWidth}
        ref={nameTextInputReference}
        returnKeyType={'done'}
        style={styles.searchTextInput}
        testID={locationsIDs.editLocationTextInput}
        useMiniKeyboard
        value={locationName}
        onChangeText={text => {
          setTextLocationName(text);
        }}
      />
      <View style={styles.bottomButtons}>
        <TWCButton
          accessibilityHint={t('saveButtonAccessibilityHint')}
          accessibilityLabel={t('saveButtonAccessibilityLabel', {
            name: locationName,
            initial: initialLocation,
          })}
          nextFocusRight={cancelButtonHandle}
          nextFocusUp={nameTextInputHandle}
          ref={saveButtonReference}
          shouldUseContextReference={false}
          style={styles.button}
          testID={locationsIDs.editLocationSaveButton}
          title={t('saveButtonText')}
          onPress={() => {
            setLocationName(locationID, locationName);
            goBack();
          }}
        />
        <TWCButton
          accessibilityHint={t('cancelButtonAccessibilityHint')}
          accessibilityLabel={t('cancelButtonAccessibilityLabel')}
          nextFocusLeft={saveButtonHandle}
          nextFocusUp={nameTextInputHandle}
          ref={cancelButtonReference}
          shouldUseContextReference={false}
          style={styles.button}
          testID={locationsIDs.editLocationCancelButton}
          title={t('cancelButtonText')}
          onPress={goBack}
        />
      </View>
    </View>
  );
};

EditLocationPanel.propTypes = {
  setLocationName: PropTypes.func.isRequired,
  setVisiblePanel: PropTypes.func.isRequired,
  data: PropTypes.array,
  locationID: PropTypes.string.isRequired,
};

EditLocationPanel.defaultProps = {
  data: null,
};

const styles = StyleSheet.create({
  bottomButtons: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'flex-start',
    marginLeft: Theme.px(-26),
  },
  button: { marginRight: Theme.px(25) },
  fullNameText: {
    color: Theme.white,
    fontSize: Theme.px(36),
    marginBottom: Theme.px(15),
    opacity: 0.7,
  },
  itemText: {
    color: Theme.white,
    fontSize: Theme.px(38),
    height: '100%',
    marginLeft: isWeb ? Theme.px(25) : Theme.px(21),
    marginTop: isWeb ? Theme.px(29) : Theme.px(21),
  },
  panel: {
    height: '100%',
  },
  searchTextInput: {
    marginLeft: Theme.px(-26),
    width: Theme.px(926),
  },
  subTitleText: {
    color: Theme.whisperWhite,
    fontSize: Theme.px(30),
    marginBottom: Theme.px(15),
    marginTop: Theme.px(60),
    opacity: 0.5,
  },
  titleText: {
    color: Theme.white,
    fontSize: Theme.px(60),
    marginBottom: Theme.px(60),
    marginTop: Theme.px(80),
  },
});
